import * as React from "react";
import { LexicalRichTextareaEditor } from "@/domains/forms";
import { validateExperience } from "../../../domains/profiles/utils/validation";

interface Experience {
  id?: number;
  employer?: string;
  title?: string;
  start_date?: string;
  end_date?: string;
  current?: boolean;
  description?: string;
  _destroy?: boolean;
  company?: string; // For backwards compatibility
  position?: string; // For backwards compatibility
  start_month?: string; // For backwards compatibility
  start_year?: string; // For backwards compatibility  
  end_month?: string; // For backwards compatibility
  end_year?: string; // For backwards compatibility
}

interface ExperienceFormProps {
  experiences: Experience[];
  onExperiencesChange: (experiences: Experience[]) => void;
  validationErrors?: { [index: number]: { [field: string]: string | null } };
  onValidationChange?: (index: number, field: string, error: string | null) => void;
  showValidationErrors?: boolean;
}

const BlankExperienceItem: Experience = {
  employer: "",
  title: "",
  start_date: "",
  end_date: "",
  current: false,
  description: "",
};

export const ExperienceForm: React.FC<ExperienceFormProps> = ({
  experiences,
  onExperiencesChange,
  validationErrors = {},
  onValidationChange,
  showValidationErrors = true,
}) => {
  const [expandedIds, setExpandedIds] = React.useState<number[]>([]);
  const newItemRef = React.useRef<HTMLDivElement>(null);

  // Auto-expand the first experience when it gets added
  React.useEffect(() => {
    if (experiences.length === 1 && !expandedIds.includes(0)) {
      setExpandedIds([0]);
    }
  }, [experiences.length, expandedIds]);

  // Add custom styles for lexical editor integration
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .lexical-editor-wrapper.is-invalid .editor-inner {
        border-color: #dc3545;
      }
      .lexical-editor-wrapper.is-invalid .editor-inner:focus-within {
        border-color: #dc3545;
        box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
      }
      .lexical-editor-wrapper .editor-container {
        width: 100%;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const addExperienceItem = () => {
    const updatedExperiences = [...experiences, BlankExperienceItem];
    onExperiencesChange(updatedExperiences);
    const newIndex = updatedExperiences.length - 1;
    setExpandedIds([...expandedIds, newIndex]);

    setTimeout(() => {
      newItemRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const removeExperienceItem = (index: number) => {
    if (window.confirm("Are you sure you want to delete this experience?")) {
      const experience = experiences[index];

      // If the experience has an ID (existing record), mark for deletion
      if (experience.id) {
        const updatedExperiences = experiences.map((exp, i) =>
          i === index ? { ...exp, _destroy: true } : exp
        );
        onExperiencesChange(updatedExperiences);
      } else {
        // If no ID (new record), remove from array entirely
        const updatedExperiences = experiences.filter((_, i) => i !== index);
        onExperiencesChange(updatedExperiences);

        // Update expanded state for remaining items
        setExpandedIds(prev =>
          prev.map(id => id > index ? id - 1 : id).filter(id => id !== index)
        );
      }
    }
  };

  const updateExperienceItem = (index: number, updatedExperience: Experience) => {
    const updatedExperiences = experiences.map((exp, i) =>
      i === index ? updatedExperience : exp
    );
    onExperiencesChange(updatedExperiences);
  };

  const validateField = (index: number, field: string, value: string) => {
    if (!onValidationChange) return;

    const testExperience = { ...experiences[index], [field]: value };
    const errors = validateExperience(testExperience);
    onValidationChange(index, field, errors[field] || null);
  };

  const toggleAccordion = (index: number) => {
    setExpandedIds((prev) =>
      prev.includes(index) ? prev.filter((id) => id !== index) : [...prev, index]
    );
  };

  return (
    <div className="accordion modern-accordion" id="experienceAccordion">
      {experiences
        .map((experience, originalIndex) => {
          // Skip items marked for deletion but preserve indexes for validation
          if (experience._destroy) return null;

          return (
            <div
              className="accordion-item"
              key={originalIndex}
              ref={originalIndex === experiences.length - 1 ? newItemRef : null}
            >
              <h2 className="accordion-header">
                {(() => {
                  const itemErrors = validationErrors[originalIndex];
                  const hasErrors = itemErrors && Object.values(itemErrors).some(e => e !== null);
                  const errorCount = itemErrors ? Object.values(itemErrors).filter(e => e !== null).length : 0;

                  return (
                    <div style={{ display: 'flex', alignItems: 'stretch', position: 'relative' }}>
                      <button
                        className={`accordion-button ${!expandedIds.includes(originalIndex) ? 'collapsed' : ''}`}
                        type="button"
                        onClick={() => toggleAccordion(originalIndex)}
                        style={{
                          cursor: 'pointer',
                          flex: 1,
                          paddingRight: '3rem',
                          backgroundColor: hasErrors ? '#fef2f2' : undefined,
                          borderLeft: hasErrors ? '4px solid #dc2626' : undefined,
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {hasErrors && (
                            <i
                              className="fas fa-exclamation-circle"
                              style={{ color: '#dc2626', fontSize: '1rem' }}
                              title={`${errorCount} validation error${errorCount > 1 ? 's' : ''}`}
                              aria-hidden="true"
                            />
                          )}
                          <span>{experience.title || 'New Experience'} at {experience.employer || 'New Employer'}</span>
                          {hasErrors && (
                            <span style={{
                              backgroundColor: '#dc2626',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              marginLeft: '0.5rem'
                            }}>
                              {errorCount} error{errorCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => removeExperienceItem(originalIndex)}
                        style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'inherit' }}
                        aria-label={`Delete experience: ${experience.title || 'New Experience'}`}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  );
                })()}
              </h2>

              <div className={`accordion-collapse collapse ${expandedIds.includes(originalIndex) ? 'show' : ''}`}>
                <div className="accordion-body">
                  <ExperienceItem
                    experience={experience}
                    index={originalIndex}
                    onUpdate={(updatedExperience) => updateExperienceItem(originalIndex, updatedExperience)}
                    onValidate={(field, value) => validateField(originalIndex, field, value)}
                    validationErrors={validationErrors[originalIndex] || {}}
                    showValidationErrors={showValidationErrors}
                  />
                </div>
              </div>
            </div>
          );
        })
        .filter(Boolean) // Remove null entries from deleted items
      }
      <div className="text-center mt-3">
        <button type="button" className="modern-btn btn-primary" onClick={addExperienceItem}>
          <i className="fas fa-plus"></i>
          Add Experience
        </button>
      </div>
    </div>
  );
};

interface ExperienceItemProps {
  experience: Experience;
  index: number;
  onUpdate: (experience: Experience) => void;
  onValidate: (field: string, value: string) => void;
  validationErrors: { [field: string]: string | null };
  showValidationErrors: boolean;
}

const ExperienceItem: React.FC<ExperienceItemProps> = ({
  experience,
  index,
  onUpdate,
  onValidate,
  validationErrors,
  showValidationErrors,
}) => {
  const [experienceState, setExperienceState] = React.useState(experience);

  // Update local state when experience prop changes
  React.useEffect(() => {
    setExperienceState(experience);
  }, [experience]);

  const handleFieldUpdate = (field: string, value: any) => {
    const updatedState = { ...experienceState, [field]: value };
    setExperienceState(updatedState);
    onUpdate(updatedState);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    handleFieldUpdate(name, type === 'checkbox' ? checked : value);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onValidate(name, value);
  };

  const getFieldClasses = (field: string) => {
    const hasError = showValidationErrors && validationErrors[field];
    if (field === 'description') {
      // For Lexical editor, return class name without 'form-control'
      return hasError ? 'is-invalid' : '';
    }
    return `form-control ${hasError ? 'is-invalid' : ''}`;
  };

  const renderValidationError = (field: string) => {
    if (!showValidationErrors || !validationErrors[field]) return null;

    return (
      <div className="invalid-feedback">
        {validationErrors[field]}
      </div>
    );
  };

  return (
    <div className="row">
      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`experience_title_${index}`} className="required">
            Job Title
          </label>
          <input
            type="text"
            className={getFieldClasses('title')}
            name="title"
            id={`experience_title_${index}`}
            placeholder="e.g. Dental Assistant"
            value={experienceState.title}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
          {renderValidationError('title')}
        </div>
      </div>

      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`experience_employer_${index}`} className="required">
            Employer
          </label>
          <input
            type="text"
            className={getFieldClasses('employer')}
            name="employer"
            id={`experience_employer_${index}`}
            placeholder="e.g. Smith Dental Clinic"
            value={experienceState.employer}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
          {renderValidationError('employer')}
        </div>
      </div>

      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`experience_start_date_${index}`} className="required">
            Start Date
          </label>
          <input
            type="date"
            className={getFieldClasses('start_date')}
            name="start_date"
            id={`experience_start_date_${index}`}
            value={experienceState.start_date}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
          />
          {renderValidationError('start_date')}
        </div>
      </div>

      <div className="col-md-6">
        <div className="modern-form-group">
          <label htmlFor={`experience_end_date_${index}`} className={!experienceState.current ? "required" : ""}>
            End Date
          </label>
          <input
            type="date"
            className={getFieldClasses('end_date')}
            name="end_date"
            id={`experience_end_date_${index}`}
            value={experienceState.end_date}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={experienceState.current}
          />
          {renderValidationError('end_date')}
        </div>
      </div>

      <div className="col-12">
        <div className="modern-checkbox-group">
          <input
            type="checkbox"
            className="form-check-input"
            name="current"
            id={`experience_current_${index}`}
            checked={experienceState.current}
            onChange={handleInputChange}
          />
          <label className="form-check-label" htmlFor={`experience_current_${index}`}>
            I currently work here
          </label>
        </div>
      </div>

      <div className="col-12">
        <div className="modern-form-group">
          <label htmlFor={`experience_description_${index}`}>
            Description
          </label>
          <div className={`lexical-editor-wrapper ${getFieldClasses('description')}`}>
            <LexicalRichTextareaEditor
              key={`experience-${experience.id || index}-description`}
              editorState={experienceState.description || ''}
              onChangeDescription={(value) => handleFieldUpdate('description', value)}
              id={`experience_description_${index}`}
              placeholder="Describe your responsibilities, achievements, and key skills used in this role..."
              minHeight="120px"
            />
          </div>
          {renderValidationError('description')}
        </div>
      </div>
    </div>
  );
};

export default ExperienceForm;
